import { Close, DeleteOutlined } from '@mui/icons-material'
import './QuoteValidation.css'
import {
  Chip,
  TableCell,
  TableRow,
  TableHead,
  Table,
  Typography,
  Button,
  TextField,
  TableBody,
  IconButton,
  TableContainer,
  Autocomplete,
} from '@mui/material'
import { DatePicker, CurrencyInput } from '../helper'
import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { manufacturerProviders } from '../lists'
import axios from 'axios'
import dayjs from 'dayjs'
import { quoteLineType } from '../types'

function QuoteValidation() {
  const navigate = useNavigate()
  const location = useLocation()
  const [validationLoading, setValidationLoading] = useState(false)
  const [totalAmount, setTotalAmount] = useState<number | null>(null)
  const [lineItems, setLineItems] = useState<quoteLineType[]>(location.state.lineItems || [])
  const [provider, setProvider] = useState<string>('')
  const [quoteNumber, setQuoteNumber] = useState<string>(location.state.quoteNumber || '')
  const quoteId = location.state.quoteId
  const [expirationDate, setExpirationDate] = useState<Date | null>(null)

  async function validateQuote() {
    setValidationLoading(true)
    lineItems.forEach(lineItem => {
      lineItem.LineNumber = Number(lineItem.LineNumber)
      lineItem.Qty = Number(lineItem.Qty) ? Number(lineItem.Qty) : 0
      lineItem.UnitCost = Number(lineItem.UnitCost) ? Number(lineItem.UnitCost) : 0
      lineItem.ExtCost = Number(lineItem.ExtCost) ? Number(lineItem.ExtCost) : 0
      lineItem.ContractCeilingPrice = Number(lineItem.ContractCeilingPrice) ? Number(lineItem.ContractCeilingPrice) : 0
    })

    const formData = new FormData()
    formData.append('file', location.state.file)

    axios
      .post(`/uploadFile/${quoteId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(() => {
        axios
          .post('/validateVendorQuote', {
            lineItems: lineItems,
            header: {
              quoteId: quoteId,
              vendorQuoteNumber: quoteNumber,
              vendorName: provider,
              expirationDate: expirationDate,
              totalAmount: totalAmount,
            },
          })
          .then(() => {
            navigate('/quote/' + quoteId)
          })
          .catch(err => {
            // add error message
            console.error(err)
          })
          .finally(() => {
            setValidationLoading(false)
          })
      })
      .catch(err => {
        console.log(err)
        setValidationLoading(false)
      })
  }

  return (
    <div className="quote-validation">
      <div className="quote-validation-tabs">
        <div className="quote-validation-tabs-left">
          <Typography variant="h6">Vendor Quote Validation</Typography>
          <Chip label={<Typography variant="h6">Quote #: {quoteNumber}</Typography>} size="medium" variant="filled" />
        </div>
        <div className="quote-validation-tabs-right">
          <Button variant="contained" onClick={validateQuote} loading={validationLoading}>
            Validate
          </Button>
          <IconButton
            onClick={() => {
              navigate('/quote/' + quoteId)
            }}
          >
            <Close />
          </IconButton>
        </div>
      </div>
      <div className="quote-validation-header">
        <Autocomplete
          fullWidth
          size="small"
          value={provider}
          onChange={(_, value) => {
            setProvider(value as string)
          }}
          options={manufacturerProviders}
          renderInput={params => <TextField {...params} label="Vendor Name (Provider)" multiline />}
        />
        <TextField
          fullWidth
          size="small"
          label="Vendor Quote #"
          value={quoteNumber}
          onChange={e => {
            setQuoteNumber(e.target.value)
          }}
        />
        <DatePicker
          fullWidth
          size="small"
          label="Expiration Date"
          value={expirationDate ? dayjs(expirationDate) : null}
          onChange={value => {
            setExpirationDate(value?.toDate() ?? null)
          }}
        />
        <CurrencyInput
          value={totalAmount}
          size="small"
          label="Total Amount"
          fullWidth
          required
          onChange={value => {
            setTotalAmount(value)
          }}
        />
      </div>
      <TableContainer className="quote-validation-table-container">
        <Table stickyHeader>
          <TableHead className="quote-validation-table-header">
            <TableRow>
              <TableCell width="6%">Line #</TableCell>
              <TableCell width="11%">Provider</TableCell>
              <TableCell width="11%">Manufacturer</TableCell>
              <TableCell width="8%">MFR Part #</TableCell>
              <TableCell width="8%">Display Part #</TableCell>
              <TableCell width="18%">Description</TableCell>
              <TableCell width="5%" align="right">
                Qty
              </TableCell>
              <TableCell width="8%" align="right">
                Unit Cost
              </TableCell>
              <TableCell width="8%" align="right">
                Ext Cost
              </TableCell>
              <TableCell width="8%" align="right">
                Contract Ceiling Price
              </TableCell>
              <TableCell width="4%"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody className="quote-validation-table-body">
            {lineItems
              .sort((a, b) => a.LineNumber - b.LineNumber)
              .map(item => (
                <TableRow key={item.LineNumber}>
                  <TableCell>{item.LineNumber}</TableCell>
                  <TableCell>
                    <Autocomplete
                      fullWidth
                      size="small"
                      value={item.Provider}
                      onChange={(_, value) => {
                        setLineItems(prev =>
                          prev.map(lineItem =>
                            lineItem.LineNumber === item.LineNumber
                              ? { ...lineItem, Provider: value as string }
                              : lineItem,
                          ),
                        )
                      }}
                      options={manufacturerProviders}
                      renderInput={params => <TextField {...params} label="Provider" multiline />}
                    />
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      fullWidth
                      size="small"
                      value={item.Manufacturer}
                      onChange={(_, value) => {
                        setLineItems(prev =>
                          prev.map(lineItem =>
                            lineItem.LineNumber === item.LineNumber
                              ? { ...lineItem, Manufacturer: value as string }
                              : lineItem,
                          ),
                        )
                      }}
                      options={manufacturerProviders}
                      renderInput={params => <TextField {...params} label="Manufacturer" multiline />}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      label="Mfr Part #"
                      multiline
                      size="small"
                      value={item.MfrPartNumber}
                      onChange={e => {
                        setLineItems(prev =>
                          prev.map(lineItem =>
                            lineItem.LineNumber === item.LineNumber
                              ? { ...lineItem, MfrPartNumber: e.target.value }
                              : lineItem,
                          ),
                        )
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      label="Display Part #"
                      multiline
                      size="small"
                      value={item.DisplayPartNumber}
                      onChange={e => {
                        setLineItems(prev =>
                          prev.map(lineItem =>
                            lineItem.LineNumber === item.LineNumber
                              ? { ...lineItem, DisplayPartNumber: e.target.value }
                              : lineItem,
                          ),
                        )
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      multiline
                      label="Description"
                      size="small"
                      value={item.Description}
                      onChange={e => {
                        setLineItems(prev =>
                          prev.map(lineItem =>
                            lineItem.LineNumber === item.LineNumber
                              ? { ...lineItem, Description: e.target.value }
                              : lineItem,
                          ),
                        )
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      label="Qty"
                      multiline
                      size="small"
                      value={item.Qty && !isNaN(item.Qty) ? item.Qty.toLocaleString() : 0}
                      onChange={e => {
                        setLineItems(prev =>
                          prev.map(lineItem =>
                            lineItem.LineNumber === item.LineNumber
                              ? { ...lineItem, Qty: Number(e.target.value.replace(/,/g, '')) }
                              : lineItem,
                          ),
                        )
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <CurrencyInput
                      fullWidth
                      label="Unit Cost"
                      size="small"
                      value={item.UnitCost ?? 0}
                      onChange={value => {
                        setLineItems(prev =>
                          prev.map(lineItem =>
                            lineItem.LineNumber === item.LineNumber ? { ...lineItem, UnitCost: value ?? 0 } : lineItem,
                          ),
                        )
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <CurrencyInput
                      fullWidth
                      label="Ext Cost"
                      size="small"
                      value={item.ExtCost && !isNaN(item.ExtCost) ? item.ExtCost : 0}
                      onChange={value => {
                        setLineItems(prev =>
                          prev.map(lineItem =>
                            lineItem.LineNumber === item.LineNumber ? { ...lineItem, ExtCost: value ?? 0 } : lineItem,
                          ),
                        )
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <CurrencyInput
                      fullWidth
                      label="Contract Ceiling Price"
                      size="small"
                      value={item.ContractCeilingPrice ?? 0}
                      onChange={value => {
                        setLineItems(prev =>
                          prev.map(lineItem =>
                            lineItem.LineNumber === item.LineNumber
                              ? { ...lineItem, ContractCeilingPrice: value ?? 0 }
                              : lineItem,
                          ),
                        )
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => {
                        setLineItems(prev => prev.filter(lineItem => lineItem.LineNumber !== item.LineNumber))
                      }}
                    >
                      <DeleteOutlined />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}

export default QuoteValidation
